import React, { useState, useEffect, useCallback } from 'react'
import { Box, IconButton, Tooltip, Typography, Grid, Divider, Menu, MenuItem, Checkbox, Button } from '@mui/material'
import { FilterButton } from '../common'
import { TableLimit } from '@/@core/components/mui/TableLimit'
import CompanySelection from '../selection/CompanySelection'
import { useRouteQuery, useTranslator } from '@/hooks'
import InputDateField from '../common/input/InputDateField'
import AppLogs from '../common/AppLogs'
import dayjs from 'dayjs'
import TransactionEmployeeStatusSelection from '../selection/TransactionEmployeeStatusSelection'
import { ActivityLogTypeEnum } from '@/@data'
import TransactionStatusSelection from '../selection/TransactionStatusSelection'
import InputDateRangerField from '../common/input/InputDateRangerField'

interface TransactionTableFilterProps {
  onSearch?: (value: string) => void
  onAddTransaction?: () => void
  columnVisibility: Record<string, boolean>
  onColumnVisibilityChange: (columns: Record<string, boolean>) => void
  isTransaction?: boolean
}

const TransactionTableFilter: React.FC<TransactionTableFilterProps> = ({
  columnVisibility,
  onColumnVisibilityChange,
  isTransaction = false
}) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const [filter, setFilter] = useState(true)
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null)

  const columns = [
    { key: 'employer', label: __('Employer') },
    { key: 'employee', label: __('Employee') },
    { key: 'eligibility_rate', label: __('Eligibility Rate') },
    { key: 'requested_transaction', label: __('Requested Transaction') },
    { key: 'total_ewa_disbursement', label: __('Total EWA Disbursement') },
    { key: 'expired_contract', label: __('Expired Contract') },
    { key: 'total_transaction_fee', label: __('Total Transaction Fee') },
    { key: 'voucher', label: __('Voucher') },
    { key: 'status', label: __('Status') }
  ]

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length

  // Only count toggleable columns (exclude first and last)
  const toggleableColumns = columns.slice(1, -1)
  const visibleToggleableCount = toggleableColumns.filter(col => columnVisibility[col.key] !== false).length

  const isAllSelected = visibleToggleableCount === toggleableColumns.length
  const isIndeterminate = visibleToggleableCount > 0 && visibleToggleableCount < toggleableColumns.length

  const handleSelectAll = (checked: boolean) => {
    const newVisibility = { ...columnVisibility }

    // Only toggle the middle columns (exclude first and last)
    toggleableColumns.forEach(col => {
      newVisibility[col.key] = checked
    })

    onColumnVisibilityChange(newVisibility)
  }

  const handleColumnToggle = (columnKey: string) => {
    const columnIndex = columns.findIndex(col => col.key === columnKey)

    // Don't allow toggling first and last columns
    if (columnIndex === 0 || columnIndex === columns.length - 1) {
      return
    }

    const newVisibility = {
      ...columnVisibility,
      [columnKey]: !columnVisibility[columnKey]
    }

    onColumnVisibilityChange(newVisibility)
  }

  const initializeMonth = useCallback(() => {
    const monthValue = searchQuery.get('month')

    if (!monthValue || monthValue.trim() === '') {
      const currentMonth = dayjs().startOf('month').format('YYYY-MM-DD')

      updateQuery({ month: currentMonth })
    }
  }, [searchQuery, updateQuery])

  useEffect(() => {
    initializeMonth()
  }, [initializeMonth])

  const [isOpenLogs, setIsOpenLogs] = useState(false)

  return (
    <Box
      sx={{
        display: 'flex',
        padding: '20px',
        gap: 4,
        flexDirection: 'column'
      }}
    >
      {filter && (
        <>
          <Box className='mb-3'>
            <Typography variant='h6' className='mb-3'>
              {__('Filters')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={isTransaction ? 4 : 6}>
                {isTransaction === true ? (
                  <InputDateField
                    value={searchQuery.get('month') || ''}
                    placeholder={__('Select month')}
                    picker='month'
                    format={'YYYY-MM'}
                    onChange={(val: string) => {
                      if (val) {
                        const firstDayOfMonth = dayjs(val).startOf('month').format('YYYY-MM-DD')

                        updateQuery({ month: firstDayOfMonth })
                      } else {
                        updateQuery({ month: undefined })
                      }
                    }}
                  />
                ) : (
                  <InputDateRangerField
                    value={
                      searchQuery.get('created_from') && searchQuery.get('created_to')
                        ? [searchQuery.get('created_from') as string, searchQuery.get('created_to') as string]
                        : undefined
                    }
                    onChange={created_at => {
                      if (created_at) {
                        updateQuery({ created_from: created_at[0], created_to: created_at[1], page: 1 })
                      } else {
                        updateQuery({ created_from: undefined, created_to: undefined, page: 1 })
                      }
                    }}
                  />
                )}
              </Grid>
              {isTransaction === true && (
                <Grid size={4}>
                  <CompanySelection
                    value={searchQuery.get('company_id') || ''}
                    onChange={company_id => {
                      updateQuery({ company_id, page: 1 })
                    }}
                    onClear={() => {
                      updateQuery({ company_id: undefined })
                    }}
                  />
                </Grid>
              )}

              {isTransaction === true ? (
                <Grid size={4}>
                  <TransactionEmployeeStatusSelection
                    value={searchQuery.get('status') || ''}
                    onSelect={status => updateQuery({ status, page: 1 })}
                    placeholder={__('All')}
                    queryParam='status'
                  />
                </Grid>
              ) : (
                <Grid size={6}>
                  <TransactionStatusSelection
                    value={searchQuery.get('status') || ''}
                    onSelect={status => updateQuery({ status, page: 1 })}
                    placeholder={__('All')}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <TableLimit />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterButton filter={filter} onFilter={() => setFilter(!filter)} />

          {isTransaction && (
            <>
              <Button
                variant='outlined'
                onClick={e => setColumnsMenuAnchor(e.currentTarget)}
                sx={{
                  minWidth: 'auto',
                  px: 4,
                  py: 2,
                  borderColor: '#e0e0e0',
                  borderRadius: '999px',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#ccc',
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                {__('Columns')} ({visibleColumnsCount})
                <i className='tabler-chevron-down ml-1' style={{ fontSize: 16 }} />
              </Button>

              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      borderRadius: 2
                    }
                  }
                }}
              >
                <MenuItem>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {__('Select All')}
                  </Typography>
                </MenuItem>
                <Divider />
                {columns.map((column, index) => {
                  const isFirstOrLast = index === 0 || index === columns.length - 1

                  return (
                    <MenuItem
                      key={column.key}
                      onClick={() => !isFirstOrLast && handleColumnToggle(column.key)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        opacity: isFirstOrLast ? 0.6 : 1,
                        cursor: isFirstOrLast ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Checkbox
                        checked={columnVisibility[column.key] || false}
                        onChange={() => !isFirstOrLast && handleColumnToggle(column.key)}
                        onClick={e => e.stopPropagation()}
                        disabled={isFirstOrLast}
                      />
                      <Typography variant='body2'>{column.label}</Typography>
                    </MenuItem>
                  )
                })}
              </Menu>
            </>
          )}

          <Tooltip title='History'>
            <IconButton
              size='small'
              onClick={() => {
                setIsOpenLogs(true)
              }}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: '#2E263D14',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              <i className='tabler-history' style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Transaction Management')}
        object_type={ActivityLogTypeEnum.Transaction}
        objectId={searchQuery.get('company_id') || ''}
      />
    </Box>
  )
}

export default TransactionTableFilter
